import React, { FC } from "react";
import { gql } from "@apollo/client";
import {
  useCanImpersonate,
  ViewAsSource,
} from "@src/features/app/hooks/useImpersonation";
import useUser from "@src/features/app/hooks/useUser";
import { IMPERSONATION_TEXT } from "@src/features/app/impersonation/impersonationStyles";
import {
  useViewAs,
  ViewAsTarget,
  viewAsLabel,
} from "@src/features/app/ViewAsDialog";
import EyeOutlineIcon from "@src/lib/icons/18px/eye-outline";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@src/lib/ui/DropdownMenu";
import { cn } from "@src/lib/utils/cn";
import { UserType } from "@src/types.generated";
import { useViewAsMenuItemPersonQuery } from "./ViewAsMenuItem.generated";

type ViewAs = ReturnType<typeof useViewAs>["viewAs"];

type ViewAsPerson = {
  id: number;
  name: string;
  userType: UserType;
  email?: string | null;
  deactivatedAt?: string | null;
};

/**
 * Whether the viewer may view as this person: not themselves and not Opus
 * staff. Deactivated people still get the item, shown off with the reason.
 * The server checks the full rule (locations, role level) on every request.
 */
export const canViewAsPerson = (
  person: Pick<ViewAsPerson, "id" | "email">,
  viewerId: number | undefined,
): boolean =>
  person.id !== viewerId && !person.email?.toLowerCase().endsWith("@opus.so");

/**
 * View as from a person's More menu. Call once per screen: `canViewAs` says
 * whether to render the items, `dialog` goes next to the menu (it outlives
 * the menu, which closes on select).
 */
export const useViewAsMenu = (
  source: ViewAsSource,
): {
  canViewAs: boolean;
  viewAs: ViewAs;
  dialog: React.ReactNode;
} => {
  const canViewAs = useCanImpersonate();
  const { viewAs, dialog } = useViewAs(source);
  return { canViewAs, viewAs, dialog };
};

type ItemProps = {
  viewAs: ViewAs;
  // A separator under the item, from the menu's other actions.
  separated?: boolean;
};

/** "View as Maria"; disabled, with the reason, for a deactivated person. */
export const ViewAsMenuItem: FC<ItemProps & { person: ViewAsPerson }> = ({
  person,
  viewAs,
  separated = false,
}) => {
  const { user } = useUser();
  if (!canViewAsPerson(person, user?.id)) return null;
  const deactivated = Boolean(person.deactivatedAt);
  const isNonAdmin =
    person.userType === UserType.Employee ||
    person.userType === UserType.Manager;
  const target: ViewAsTarget = {
    id: person.id,
    name: person.name,
    userType: person.userType,
  };
  return (
    <>
      <DropdownMenuItem
        disabled={deactivated}
        onSelect={() =>
          // After the menu closes, so the dialog takes focus.
          requestAnimationFrame(() =>
            viewAs(
              target,
              // Trainees and managers land on My profile, as before.
              isNonAdmin ? { redirectTo: "/my-profile" } : undefined,
            ),
          )
        }
        className="items-start"
      >
        <EyeOutlineIcon
          className={cn("mt-0.5 size-4 shrink-0", IMPERSONATION_TEXT)}
          aria-hidden
        />
        <span className="flex flex-col">
          {viewAsLabel(person.name)}
          {deactivated && (
            <span className="text-xs-v4 text-text-secondary">
              You can&apos;t view as deactivated people.
            </span>
          )}
        </span>
      </DropdownMenuItem>
      {separated && <DropdownMenuSeparator />}
    </>
  );
};

/**
 * For a table row's menu: fetches this one person when the menu opens,
 * rather than for every row on the page.
 */
export const RowViewAsMenuItem: FC<ItemProps & { personId: number }> = ({
  personId,
  ...props
}) => {
  const { data } = useViewAsMenuItemPersonQuery({
    variables: { id: personId },
  });
  const person = data?.AdminEmployee;
  return person ? <ViewAsMenuItem person={person} {...props} /> : null;
};

gql`
  query ViewAsMenuItemPerson($id: Int!) {
    AdminEmployee(id: $id) {
      id
      name
      userType
      email
      deactivatedAt
    }
  }
`;
